import {ActionIcon, Badge, Divider, Group, Image, Menu, Skeleton, Stack, Text, Tooltip} from '@mantine/core';
import {IconDotsVertical, IconEdit, IconTrash, IconCircleXFilled, IconCircleCheckFilled, IconEye, IconPencil, IconDatabaseOff, IconCurrencyDollarCanadian, IconCurrencyDollar, IconKey} from '@tabler/icons-react';
import DataTable, {TableStyles} from 'react-data-table-component';
import dayjs from "dayjs";
import { useTranslation } from 'react-i18next';
import Avatar from 'react-avatar';
import { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });
const customStyles: TableStyles = {
    table: {
        style: {
            minHeight: 380,
            border: "2px solid #E0E2E7",
            borderRadius: "8px"
        },
    },
    headRow: {
        style: {
            borderRadius: "8px 8px 0 0",
            ":nth-child(1)": {
                borderRadius: "0 8px 0 0",
            },
            ":nth-last-child(1)": {
                borderRadius: "8px 0 0 0"
            }
        },
    },
    headCells: {
        style: {
            fontWeight: 800,
            fontSize: 14,
            background: "#F9F9FC",
            height: "56px",
            ":nth-child(1)": {
                background: "#F9F9FC",
            }
        }
    },
    cells: {
        style: {
            height: "60px"
        }
    }
};

type Props = {
    setSelectedData: (data: any) => void;

    setOpenEditModal: (status: boolean) => void;
    setOpenDeleteModal: (status: boolean) => void;
    setOpenShowModal: (status: boolean) => void;
    setOpenShowInvoice: (status: boolean) => void;
    setOpenEditPassModal: (status: boolean) => void;
    data?: any;
    loading?: boolean;
};


export const UsersTabel = ({data, loading, setSelectedData, setOpenEditModal, setOpenDeleteModal, setOpenShowModal, setOpenShowInvoice, setOpenEditPassModal}: Props) => {
    const { t } = useTranslation();
    const [role, setRole] = useState("");

    useEffect(() => {
        if (cookies.get('role')) {
            setRole(cookies.get('role'))
        }
    }, [cookies.get('role')])

    const columns = [
        {name: t('tables.users.row01'), selector: (row: any, index: number) => (
            <Group justify='flex-start' align='center' gap={8}>
                <Avatar name={row.name} size="30" round={true} />
                <Stack justify='center' align='flex-start' gap={5}>
                    <Text size='14px' c={"#333843"} fw={500}>{row.name}</Text>
                </Stack>
            </Group>
        ), minWidth: "200px"},
        {name: t('tables.users.row02'), selector: (row: any, index: number) => row?.email, minWidth: "200px", sortable: true, sortFunction: (a: any, b: any) => a?.price > b?.price ? 1 : -1 },
        {name: t('tables.users.row03'), selector: (row: any, index: number) => row?.phone, minWidth: "200px", sortable: true, sortFunction: (a: any, b: any) => a?.price > b?.price ? 1 : -1 },
        {name: t('tables.users.row04'), selector: (row: any, index: number) => (
            row?.role === "admin" ? <Badge radius={'sm'} color='gray' variant='light'>{t('tables.users.badge01')}</Badge> 
                : row?.role === "confirmed" ? <Badge radius={'sm'} color='gray' variant='light'>{t('tables.users.badge02')}</Badge>
                    : row?.role === "marketer" ? <Tooltip label={row?.referralCode}><Badge style={{cursor: 'pointer'}} radius={'sm'} color='gray' variant='light'>{t('tables.users.badge03')}</Badge></Tooltip> : null
        ), minWidth: "200px", sortable: true, sortFunction: (a: any, b: any) => a?.price > b?.price ? 1 : -1 },
        {name:t('tables.users.row05'), selector: (row: any) => dayjs(row.createdAt).locale("ar").fromNow(), minWidth: "200px", sortable: true, sortFunction: (a: any, b: any) => a?.createdAt > b?.createdAt ? 1 : -1 },

        {name: t('tables.users.row06'), cell: (row: any) => (
            <Group wrap={"nowrap"} justify={"center"} w={"100%"} gap={2}>
                {row?.referralCode && row?.referralCode !== ""
                    ? <>
                        <Tooltip label={"إحصائيات المسوق"}>
                            <ActionIcon
                                variant={"transparent"} color={"#667085"}
                                onClick={() => {
                                    setSelectedData(row)
                                    setOpenShowModal(true)
                                }}
                            >
                                <IconEye size="1.125rem" />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={"فواتر المسوق"}>
                            <ActionIcon
                                variant={"transparent"} color={"#667085"}
                                onClick={() => {
                                    setSelectedData(row)
                                    setOpenShowInvoice(true)
                                }}
                            >
                                <IconCurrencyDollar size="1.125rem" />
                            </ActionIcon>
                        </Tooltip>
                    </>
                    : null
                }
                
                <Menu shadow="md" width={180}>
                    <Menu.Target>
                        <ActionIcon variant={"transparent"} color={"darck"}>
                            <IconDotsVertical size="1.125rem" />
                        </ActionIcon>
                    </Menu.Target>

                    <Menu.Dropdown>
                        {["admin"].includes(role)
                            ? <Menu.Item
                                leftSection={<IconKey size={14} />}
                                onClick={() => {
                                    setSelectedData(row)
                                    setOpenEditPassModal(true)
                                }}
                            >{"تعديل كلمة المرور"}</Menu.Item>
                            : null
                        }
                        <Divider />
                        <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={() => {
                                setSelectedData(row)
                                setOpenEditModal(true)
                            }}
                        >{t('tables.orders.edit')}</Menu.Item>
                        
                        <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            onClick={() => {
                                setSelectedData(row)
                                setOpenDeleteModal(true)
                            }}
                        >{t('tables.orders.delete')}</Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Group>
        ), allowOverflow: true, button: true, minWidth: "130px" }
    ];

    return (
        <DataTable
            // @ts-ignore
            columns={columns}
            data={data}
            customStyles={customStyles}
            highlightOnHover
            pagination={true}
            persistTableHead={true}
            progressPending={loading || false}
            progressComponent={<LoadingTable />}
            noDataComponent={
                <Stack align='center' justify='center' p={10}>
                    <Image src={"/emptyData.png"} mah={150} w={"auto"} />
                    <Text size='14px' c={"#667085"}>{t('tables.emptyLable')}</Text>
                </Stack>
            }
            paginationComponentOptions={{
                rowsPerPageText: t('tables.rangeSeparatorText'),
                rangeSeparatorText: t('tables.rangeSeparatorText')
            }}
            paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50, 75, 100, 200]}
            paginationPerPage={5}
        />
    );
};


const LoadingTable = () => {
    const numRows = [0,0,0];
    return (
        <Stack w={"100%"} p={0} m={0} gap={0}>
            {numRows.map((item, index) => (
                <RowSkeleton key={index}/>
            ))}
        </Stack>
    )
}

const RowSkeleton = () => {
    return (
        <Group w={"100%"} mih={48} align='stretch' gap={0} wrap='nowrap' style={{borderBottom: "2px solid #E0E2E7"}}>
            <Group h={60} miw={"40%"} px={16} justify='flex-start' align='center' gap={8} wrap='nowrap'>
                <Skeleton width={44} height={44} />
                <Stack justify='center' align='flex-start' gap={5}>
                    <Skeleton height={14} width={100} />
                </Stack>
            </Group>
            <Group h={60} miw={"25%"} px={16} justify='flex-start' align='center'>
                <Skeleton height={13} width={100} />
            </Group>
            <Group h={60} miw={"25%"} px={16} justify='flex-start' align='center'>
                <Skeleton height={13} width={60} />
            </Group>
            <Group h={60} miw={"25%"} px={16} justify='flex-start' align='center'>
                <Skeleton height={13} width={100} />
            </Group>
            <Group h={60} miw={"25%"} px={16} justify='flex-start' align='center'>
                <Skeleton height={13} width={60} />
            </Group>
            <Group h={60} miw={"10%"} px={16} justify='flex-start' align='center'>
                <Group wrap={"nowrap"} justify={"center"} w={"100%"} gap={2}>
                    <Skeleton height={18} width={18} />
                    <Skeleton height={18} width={18} />
                </Group>
            </Group>
        </Group>
    );
}